import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Dropdown, List, Image, Icon, Segment, Header, Dimmer, Loader, Message, Label } from 'semantic-ui-react';
import { useStore } from '../../app/stores/store';

interface InvitableFriend {
  userId: string;
  fullName: string;
  imageUrl: string;
  isAlreadyInvited: boolean;
  isParticipant: boolean;
}

interface GameInvitation {
  id: string;
  gameId: string;
  invitedUserId: string;
  invitedByUserId: string;
  invitedUserFullName: string;
  invitedUserImageUrl: string;
  invitedByUserFullName: string;
  invitedAt: Date;
  status: string;
}

interface Props {
  gameId: string;
}

export default observer(function InviteFriendsToGame({ gameId }: Props) {
  const { gameInvitationStore } = useStore();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedFriendIds, setSelectedFriendIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      loadData();
    }
    return () => {
      if (!open) {
        gameInvitationStore.clearInvitableFriends();
        gameInvitationStore.clearGameInvitations();
      }
    };
  }, [open, gameId]);

  const loadData = async () => {
    setError(null);
    await Promise.all([
      gameInvitationStore.loadInvitableFriends(gameId),
      gameInvitationStore.loadGameInvitations(gameId)
    ]);
  };

  const handleOpen = () => {
    setOpen(true);
    setSelectedFriendIds([]);
    setSuccess(false);
    setError(null);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedFriendIds([]);
    setSuccess(false);
    setError(null);
  };

  const handleSendInvitations = async () => {
    if (selectedFriendIds.length === 0) {
      setError(t('gameInvitations.selectAtLeastOne'));
      return;
    }

    setError(null);
    const result = await gameInvitationStore.sendInvitations(gameId, selectedFriendIds);

    if (result) {
      setSuccess(true);
      setSelectedFriendIds([]);
      setTimeout(() => setSuccess(false), 3000);
    } else {
      setError(t('gameInvitations.sendError'));
    }
  };

  const getDropdownOptions = () => {
    return gameInvitationStore.invitableFriends
      .filter(friend => !friend.isAlreadyInvited && !friend.isParticipant)
      .map(friend => ({
        key: friend.userId,
        value: friend.userId,
        text: friend.fullName,
        image: { avatar: true, src: friend.imageUrl || '/assets/user-placeholder.png' }
      }));
  };

  const getInvitableCount = () => {
    return gameInvitationStore.invitableFriends.filter(
      f => !f.isAlreadyInvited && !f.isParticipant
    ).length;
  };

  return (
    <Modal
      trigger={
        <Button
          icon
          labelPosition='left'
          inverted
          size='large'
          onClick={handleOpen}
          style={{ borderRadius: '50px' }}
        >
          <Icon name='user plus' />
          {t('gameInvitations.inviteFriends')}
        </Button>
      }
      open={open}
      onClose={handleClose}
      size='small'
      closeIcon
    >
      <Modal.Header>
        <Icon name='users' />
        {t('gameInvitations.inviteFriendsToGame')}
      </Modal.Header>

      <Modal.Content scrolling style={{ minHeight: '400px', maxHeight: '70vh' }}>
        {gameInvitationStore.loading ? (
          <Dimmer active inverted>
            <Loader inverted>{t('gameInvitations.loading')}</Loader>
          </Dimmer>
        ) : (
          <>
            {/* Friend Selection Dropdown */}
            <Segment>
              <Header as='h4'>
                <Icon name='search' />
                {t('gameInvitations.selectFriends')}
              </Header>

              {getInvitableCount() > 0 ? (
                <Dropdown
                  placeholder={t('gameInvitations.searchFriends')}
                  fluid
                  multiple
                  search
                  selection
                  options={getDropdownOptions()}
                  value={selectedFriendIds}
                  onChange={(e, { value }) => {
                    setSelectedFriendIds(value as string[]);
                    setError(null);
                  }}
                  disabled={gameInvitationStore.submitting}
                  style={{ marginBottom: '1em' }}
                />
              ) : (
                <Message info>
                  <Icon name='info circle' />
                  {t('gameInvitations.allFriendsInvited')}
                </Message>
              )}

              {selectedFriendIds.length > 0 && (
                <div style={{ marginTop: '1em' }}>
                  <Label>
                    {t('gameInvitations.selected')}: {selectedFriendIds.length}
                  </Label>
                </div>
              )}
            </Segment>

            {/* Success Message */}
            {success && (
              <Message positive>
                <Icon name='check circle' />
                {t('gameInvitations.invitationsSent')}
              </Message>
            )}

            {/* Error Message */}
            {error && (
              <Message negative>
                <Icon name='exclamation circle' />
                {error}
              </Message>
            )}

            {/* Already Invited Users List */}
            <Segment>
              <Header as='h4'>
                <Icon name='mail' />
                {t('gameInvitations.alreadyInvited')} ({gameInvitationStore.gameInvitations.length})
              </Header>

              {gameInvitationStore.gameInvitations.length === 0 ? (
                <Message info>
                  <Icon name='info circle' />
                  {t('gameInvitations.noInvitationsYet')}
                </Message>
              ) : (
                <List divided verticalAlign='middle'>
                  {gameInvitationStore.gameInvitations.map(invitation => (
                    <List.Item key={invitation.id}>
                      <Image
                        avatar
                        src={invitation.invitedUserImageUrl || '/assets/user-placeholder.png'}
                      />
                      <List.Content>
                        <List.Header>{invitation.invitedUserFullName}</List.Header>
                        <List.Description>
                          <Icon name='user' size='small' />
                          {t('gameInvitations.invitedBy')}: {invitation.invitedByUserFullName}
                          <br />
                          <Icon name='clock' size='small' />
                          {new Date(invitation.invitedAt).toLocaleDateString()}
                          {' '}
                          {new Date(invitation.invitedAt).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </List.Description>
                      </List.Content>
                      <List.Content floated='right'>
                        <Label
                          size='small'
                          color={
                            invitation.status === 'Accepted' ? 'green' :
                              invitation.status === 'Declined' ? 'red' :
                                'yellow'
                          }
                        >
                          {t(`gameInvitations.status.${invitation.status.toLowerCase()}`)}
                        </Label>
                      </List.Content>
                    </List.Item>
                  ))}
                </List>
              )}
            </Segment>
          </>
        )}
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={handleClose} disabled={gameInvitationStore.submitting}>
          <Icon name='close' />
          {t('gameInvitations.cancel')}
        </Button>
        <Button
          primary
          onClick={handleSendInvitations}
          disabled={selectedFriendIds.length === 0 || gameInvitationStore.submitting}
          loading={gameInvitationStore.submitting}
        >
          <Icon name='send' />
          {gameInvitationStore.submitting
            ? t('gameInvitations.sending')
            : `${t('gameInvitations.sendInvitations')} (${selectedFriendIds.length})`
          }
        </Button>
      </Modal.Actions>
    </Modal>
  );
});